import {
  getRetailerToken,
  useRetailerStore,
} from "@faire/retailer-visitor-shared/stores/domain/RetailerStore";
import { useQuery as useProtectedBrandsQuery } from "@faire/web-api--source/api/retailer/retailerToken/zip-code-protected-brands/get-hooks";
import { trackZipCodeProtectionBrandBlocked } from "@faire/web-api--source/events/zipCodeProtection/actionUnknown/brandBlocked";
import { useCallback, useMemo } from "react";

/**
 * Faire protection can be enabled per brand/maker and is awarded to one
 * retailer per zipcode / postal code for a 90 day period.
 *
 * This hook allows one to query whether or not the current logged in
 * retailer is able to place an order from a particular maker/brand
 *
 * NOTE: this logic does NOT take into account other reasons a retailer
 * may not be able to order from a maker (like residing in a forbidden state
 * for that maker)
 */
export const useFaireProtection = () => {
  const { isValidRetailer } = useRetailerStore(["isValidRetailer"]);
  const {
    data,
    isFetching: isLoading,
    refetch,
  } = useProtectedBrandsQuery(getRetailerToken(), {
    enabled: isValidRetailer,
    staleTime: 1000 * 60 * 3,
    refetchOnMount: false,
  });
  const forbiddenBrandTokens = useMemo(
    () => data?.brand_tokens ?? [],
    [data?.brand_tokens]
  );
  const isBrandTokenForbidden = useCallback(
    (brandToken: string, options?: { trackReason?: true }): boolean => {
      const isBlocked = data?.brand_tokens?.includes(brandToken) ?? false;
      if (isBlocked && options?.trackReason) {
        const blockedReasons =
          data?.blocked_reasons[brandToken]?.reason_codes ?? [];
        blockedReasons.forEach((reason) =>
          trackZipCodeProtectionBrandBlocked(brandToken, reason.toString())
        );
      }
      return isBlocked;
    },
    [data]
  );

  const expirationTime = useCallback(
    (brandToken: string): number | undefined => {
      return data?.protected_for_retailer?.[brandToken];
    },
    [data?.protected_for_retailer]
  );

  return {
    isLoading,
    forbiddenBrandTokens,
    zipCode: data?.zip_code,
    expirationTime,
    isBrandTokenForbidden,
    refetchFaireProtection: refetch,
  };
};
